import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MpsLabelPrinterService } from '../Services/mps-label-printer.service';
import { NsTrayPrintService } from '../Services/ns-tray-print.service';
import { MpsBatchForLabels, bagPatientName } from '../Services/mps-label-builder';

interface BagRow {
  bagId: string;
  patientName: string;
  checked: boolean;
}

/**
 * "Reprint bag labels" dialog for MPS batches: pick bags, then each is printed again (asking
 * how many physical bags, as the first print did). Used by the scan screen and Batch Pick verify.
 */
@Component({
  selector: 'app-bag-reprint-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './bag-reprint-panel.component.html',
  styleUrl: './bag-reprint-panel.component.css',
})
export class BagReprintPanelComponent implements OnInit, OnChanges {
  @Input() batch: MpsBatchForLabels = {};
  @Output() closed = new EventEmitter<void>();
  /** The bags whose labels all printed, after each Print. */
  @Output() printed = new EventEmitter<string[]>();

  rows: BagRow[] = [];
  printing = false;

  constructor(private printer: MpsLabelPrinterService, readonly tray: NsTrayPrintService) {}

  /** NS Tray has printers but none is chosen (a new PC, or the saved one was renamed). */
  get needsPrinter(): boolean {
    return !this.tray.selectedPrinter && (this.tray.availablePrinters?.length ?? 0) > 0;
  }

  choosePrinter(name: string): void {
    if (name) this.tray.setPrinter(name);
  }

  ngOnInit(): void { this.buildRows(); }

  ngOnChanges(): void { this.buildRows(); }

  setAll(checked: boolean): void {
    this.rows.forEach(r => (r.checked = checked));
  }

  /** Prints the ticked bags. Closes when all printed; otherwise only the failed bags stay ticked. */
  async print(): Promise<void> {
    if (this.printing) return;
    const chosen = this.rows.filter(r => r.checked).map(r => r.bagId);
    if (chosen.length === 0) { this.closed.emit(); return; }
    this.printing = true;
    let printedIds: string[];
    try {
      printedIds = await this.printer.printBags(this.batch, chosen);
    } finally {
      this.printing = false;
    }
    if (printedIds.length > 0) this.printed.emit(printedIds);
    const printed = new Set(printedIds);
    this.rows.forEach(r => { if (printed.has(r.bagId)) r.checked = false; });
    if (chosen.every(id => printed.has(id))) this.closed.emit();
  }

  cancel(): void {
    this.closed.emit();
  }

  /** One row per bag; a refresh with the same bags keeps the pharmacist's ticks. */
  private buildRows(): void {
    const ticked = new Map(this.rows.map(r => [r.bagId, r.checked]));
    this.rows = (this.batch.bags ?? []).map(bag => ({
      bagId: bag.bagId,
      patientName: bagPatientName(this.batch, bag.bagId),
      checked: ticked.get(bag.bagId) ?? true,
    }));
  }
}
